/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  DIGITACAO_STATUS,
  STATUS_COLOR,
  STATUS_LABELS,
  getMetric,
  sumMetrics,
} from './constants';

const TOTAL_KEY = 'TOTAL';

const CARDS = [
  { key: TOTAL_KEY, label: 'Total', icon: <FormatListBulletedIcon /> },
  {
    key: DIGITACAO_STATUS.AGUARDANDO_DIGITACAO,
    label: 'Aguardando',
    icon: <HourglassEmptyIcon />,
  },
  {
    key: DIGITACAO_STATUS.EM_DIGITACAO,
    label: 'Em Digitação',
    icon: <KeyboardIcon />,
  },
  {
    key: DIGITACAO_STATUS.DIGITACAO_FINALIZADA,
    label: 'Finalizada',
    icon: <CheckCircleOutlineIcon />,
  },
  {
    key: DIGITACAO_STATUS.EXECUTADA_GDIS,
    label: 'Executada GDIS',
    icon: <TaskAltIcon />,
  },
];

const resolveColor = (theme, colorKey) => {
  if (colorKey === 'default' || !theme.palette[colorKey]) {
    return theme.palette.text.secondary;
  }
  return theme.palette[colorKey].main;
};

const formatNumber = (n) => (n ?? 0).toLocaleString('pt-BR');

const KpiCell = ({ config, metric, active, onClick }) => {
  const colorKey = STATUS_COLOR[config.key] || 'default';
  const label = config.key === TOTAL_KEY ? 'Total' : STATUS_LABELS[config.key];

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        flex: 1,
        minWidth: 190,
        justifyContent: 'flex-start',
        textAlign: 'left',
        px: 2.5,
        py: 2,
        borderRadius: 1,
        transition: 'background-color 0.2s ease',
        backgroundColor: (theme) =>
          active ? `${resolveColor(theme, colorKey)}14` : 'transparent',
        '&:hover': {
          backgroundColor: (theme) => `${resolveColor(theme, colorKey)}0F`,
        },
      }}
    >
      <Stack direction="row" spacing={1.75} alignItems="center" sx={{ width: '100%' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (theme) => resolveColor(theme, colorKey),
            backgroundColor: (theme) => `${resolveColor(theme, colorKey)}1F`,
            border: (theme) =>
              active ? `2px solid ${resolveColor(theme, colorKey)}` : '2px solid transparent',
          }}
        >
          {React.cloneElement(config.icon, { sx: { fontSize: 22 } })}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
            noWrap
            title={label}
          >
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
            {formatNumber(metric.nsirs)} NSIRs
          </Typography>
          {metric.hasBreakdown ? (
            <>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, lineHeight: 1.35 }}
                color="text.primary"
              >
                {formatNumber(metric.servicosDigitados)}/{formatNumber(metric.servicosTotal)}{' '}
                serviços
              </Typography>
              {metric.servicosPendentes > 0 && (
                <Typography
                  variant="caption"
                  sx={{ lineHeight: 1.35, fontWeight: 600 }}
                  color="warning.main"
                >
                  {formatNumber(metric.servicosPendentes)} pendentes
                </Typography>
              )}
            </>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, lineHeight: 1.35 }}
              color="text.primary"
            >
              {formatNumber(metric.servicosTotal)} serviços
            </Typography>
          )}
        </Box>
      </Stack>
    </ButtonBase>
  );
};

KpiCell.propTypes = {
  config: PropTypes.object.isRequired,
  metric: PropTypes.shape({
    nsirs: PropTypes.number,
    servicos: PropTypes.number,
    servicosTotal: PropTypes.number,
    servicosDigitados: PropTypes.number,
    servicosPendentes: PropTypes.number,
    hasBreakdown: PropTypes.bool,
  }).isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

KpiCell.defaultProps = {
  active: false,
};

const KpiSkeleton = () => (
  <Box sx={{ flex: 1, minWidth: 190, px: 2.5, py: 2 }}>
    <Stack direction="row" spacing={1.75} alignItems="center">
      <Skeleton variant="circular" width={44} height={44} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="60%" height={20} />
        <Skeleton width="40%" height={16} />
        <Skeleton width="55%" height={16} />
        <Skeleton width="45%" height={14} />
      </Box>
    </Stack>
  </Box>
);

const KpiStrip = ({ resumo, loading, activeStatus, onSelectStatus }) => {
  const { filtrado } = resumo;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        '& > *:not(:first-of-type)': {
          borderLeft: { md: 1 },
          borderTop: { xs: 1, md: 0 },
          borderColor: { xs: 'divider', md: 'divider' },
        },
      }}
    >
      {loading
        ? CARDS.map((c) => <KpiSkeleton key={c.key} />)
        : CARDS.map((config) => {
            const metric =
              config.key === TOTAL_KEY
                ? sumMetrics(filtrado)
                : getMetric(filtrado, config.key);
            const isTotal = config.key === TOTAL_KEY;
            const active = isTotal ? activeStatus == null : activeStatus === config.key;
            return (
              <KpiCell
                key={config.key}
                config={config}
                metric={metric}
                active={active}
                onClick={() => {
                  if (isTotal) {
                    onSelectStatus(null);
                  } else {
                    onSelectStatus(activeStatus === config.key ? null : config.key);
                  }
                }}
              />
            );
          })}
    </Paper>
  );
};

KpiStrip.propTypes = {
  resumo: PropTypes.shape({
    filtrado: PropTypes.object,
    total: PropTypes.object,
  }).isRequired,
  loading: PropTypes.bool,
  activeStatus: PropTypes.string,
  onSelectStatus: PropTypes.func.isRequired,
};

KpiStrip.defaultProps = {
  loading: false,
  activeStatus: null,
};

export default KpiStrip;
